import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ref, get, set } from 'firebase/database';
import { realtimeDb } from '../firebase/config';
import { FaEye, FaClock } from 'react-icons/fa';
import './RecentlyViewed.css';

const RecentlyViewed = ({ currentProduct = null, maxItems = 6 }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [recentProducts, setRecentProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadRecentlyViewed = useCallback(async () => {
    try {
      let products = [];
      
      if (currentUser) {
        // Load from Firebase for logged-in users
        const recentRef = ref(realtimeDb, `recentlyViewed/${currentUser.uid}`);
        const snapshot = await get(recentRef);
        
        if (snapshot.exists()) {
          products = snapshot.val();
        }
      } else {
        // Load from localStorage for guest users
        const localRecent = localStorage.getItem('recentlyViewed');
        if (localRecent) {
          try {
            products = JSON.parse(localRecent);
          } catch (error) {
            console.error('Error parsing recently viewed from localStorage:', error);
          }
        }
      }
      
      // Ensure products is an array and limit items
      if (Array.isArray(products)) {
        setRecentProducts(products.slice(0, maxItems));
      } else {
        setRecentProducts([]);
      }
    } catch (error) {
      console.error('Error loading recently viewed products:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser, maxItems]);

  const addToRecentlyViewed = useCallback(async (product) => {
    try {
      const productData = {
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.image,
        category: product.category,
        type: product.type,
        viewedAt: Date.now()
      };

      let updatedProducts = [];
      
      if (currentUser) {
        // Get current products from Firebase
        const recentRef = ref(realtimeDb, `recentlyViewed/${currentUser.uid}`);
        const snapshot = await get(recentRef);
        
        if (snapshot.exists()) {
          updatedProducts = snapshot.val();
        }
      } else {
        // Get current products from localStorage
        const localRecent = localStorage.getItem('recentlyViewed');
        if (localRecent) {
          try {
            updatedProducts = JSON.parse(localRecent);
          } catch (error) {
            console.error('Error parsing recently viewed from localStorage:', error);
          }
        }
      }

      // Ensure it's an array
      if (!Array.isArray(updatedProducts)) {
        updatedProducts = [];
      }

      // Remove existing product if it exists
      updatedProducts = updatedProducts.filter(p => p.id !== product.id);
      
      // Add new product to the beginning
      updatedProducts.unshift(productData);
      
      // Limit to maxItems
      updatedProducts = updatedProducts.slice(0, maxItems);
      
      if (currentUser) {
        // Save to Firebase for logged-in users
        await set(ref(realtimeDb, `recentlyViewed/${currentUser.uid}`), updatedProducts);
      } else {
        // Save to localStorage for guest users
        localStorage.setItem('recentlyViewed', JSON.stringify(updatedProducts));
      }
      
      // Update local state
      setRecentProducts(updatedProducts);
    } catch (error) {
      console.error('Error adding to recently viewed:', error);
    }
  }, [currentUser, maxItems]);

  // Load recently viewed products
  useEffect(() => {
    loadRecentlyViewed();
  }, [loadRecentlyViewed]);

  // Add current product to recently viewed
  useEffect(() => {
    if (currentProduct) {
      addToRecentlyViewed(currentProduct);
    }
  }, [currentProduct, addToRecentlyViewed]);

  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  const formatTimeAgo = (timestamp) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  if (loading) {
    return (
      <div className="recently-viewed">
        <div className="recently-viewed-header">
          <h3>Recently Viewed</h3>
        </div>
        <div className="recently-viewed-loading">Loading...</div>
      </div>
    );
  }

  if (recentProducts.length === 0) {
    return null; // Don't show component if no recent products
  }

  return (
    <div className="recently-viewed">
      <div className="recently-viewed-header">
        <h3>
          <FaEye className="header-icon" />
          Recently Viewed
        </h3>
        <span className="product-count">{recentProducts.length} items</span>
      </div>
      
      <div className="recently-viewed-grid">
        {recentProducts.map((product) => (
          <div 
            key={product.id} 
            className="recent-product-card"
            onClick={() => handleProductClick(product.id)}
          >
            <div className="product-image-container">
              <img 
                src={product.image} 
                alt={product.name}
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/120x120?text=Product';
                }}
              />
              <div className="viewed-time">
                <FaClock />
                {formatTimeAgo(product.viewedAt)}
              </div>
            </div>
            
            <div className="product-info">
              <h4 className="product-name">{product.name}</h4>
              <div className="product-category">{product.category}</div>
              <div className="product-price">₹{product.price}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RecentlyViewed; 